import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Sparkles, MapPin, Users, Trash2, ArrowUpRight } from "lucide-react";
import { api, formatApiErrorDetail, formatCompactNumber } from "@/lib/api";

const STATUSES = ["", "draft", "live", "archived"];
const CATEGORIES = ["Luxury", "Premium", "Mainstream"];
const TIERS = ["Tier 1", "Tier 2", "Tier 3"];

export default function AdminMalls() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const { data } = await api.get("/super-admin/malls", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load malls");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  async function del(id) {
    if (!window.confirm("Delete this mall? (Only allowed when no ad spaces are linked.)")) return;
    try {
      await api.delete(`/super-admin/malls/${id}`);
      toast.success("Mall deleted");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  return (
    <div data-testid="admin-malls">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline">Console · Malls</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl tracking-tighter">Mall Network</h1>
          <p className="mt-1 text-neutral-400 text-sm">Add, ingest with AI, and assign mall admins.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="malls-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search malls…"
              className="pl-9 max-w-[260px]"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="max-w-[160px]" data-testid="malls-status">
            {STATUSES.map((s) => <option key={s} value={s}>{s ? s : "All statuses"}</option>)}
          </select>
          <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="malls-create-btn">
            <Plus size={14} /> New mall
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-neutral-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-10 surface p-12 text-center text-neutral-500" data-testid="malls-empty">
          No malls match. <button onClick={() => setShowCreate(true)} className="text-amber-500 underline">Add the first mall</button>.
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((m) => (
            <Link key={m.id} to={`/admin/malls/${m.id}`} className="surface p-5 hover:border-amber-500/40 transition group" data-testid={`mall-card-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-amber-500 font-semibold">{m.category || "—"} · {m.tier || "—"}</div>
                  <div className="mt-1 font-display text-xl truncate">{m.name}</div>
                  <div className="text-xs text-neutral-400 flex items-center gap-1 mt-1"><MapPin size={11} /> {m.city}, {m.state || "India"}</div>
                </div>
                <StatusChip status={m.status} />
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 text-xs">
                <Stat label="Spaces" value={m.spot_count ?? 0} />
                <Stat label="Available" value={m.live_count ?? 0} />
                <Stat label="Footfall/mo" value={formatCompactNumber(m.monthly_footfall || 0)} />
              </div>
              <div className="mt-5 flex items-center justify-between text-[11px] text-neutral-500">
                <span>{m.area_sqft ? `${m.area_sqft.toLocaleString()} sqft` : "Sqft TBD"}</span>
                <button onClick={(e) => { e.preventDefault(); del(m.id); }} className="hover:text-red-400" data-testid={`mall-del-${m.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="mt-3 text-[11px] text-amber-500 inline-flex items-center gap-1">
                Manage <ArrowUpRight size={11} />
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && <CreateMallModal onClose={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/[0.03] border border-white/5 p-2">
      <div className="font-display text-lg">{value}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    live: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    draft: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    archived: "bg-neutral-500/15 text-neutral-400 border-neutral-500/30",
  };
  const cls = map[status] || map.draft;
  return <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${cls}`}>{status || "draft"}</span>;
}

function CreateMallModal({ onClose }) {
  const [form, setForm] = useState({
    name: "", city: "", state: "", tier: "Tier 1", category: "Premium",
    monthly_footfall: 0, area_sqft: "", address: "", description: "",
  });
  const [saving, setSaving] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [createdId, setCreatedId] = useState(null);

  async function create() {
    if (!form.name || !form.city) {
      toast.error("Mall name and city are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, area_sqft: form.area_sqft ? Number(form.area_sqft) : null, monthly_footfall: Number(form.monthly_footfall) || 0 };
      const { data } = await api.post("/super-admin/malls", payload);
      toast.success("Mall created");
      setCreatedId(data.id);
      return data.id;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return null;
    } finally { setSaving(false); }
  }

  async function createAndIngest() {
    const id = createdId || (await create());
    if (!id) return;
    setAiRunning(true);
    try {
      const { data } = await api.post(`/super-admin/malls/${id}/ai-ingest`, {});
      const fields = Object.keys(data.applied || {}).length;
      toast.success(`AI filled ${fields} fields`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI ingest failed");
    } finally { setAiRunning(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="create-mall-modal">
      <div className="surface max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="overline">Add a mall</div>
        <h2 className="mt-2 font-display text-3xl">New mall</h2>
        <p className="mt-1 text-sm text-neutral-400">Fill name + city, then optionally let AI auto-ingest area, brands, owners, address.</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Mall name *"><input data-testid="m-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="City *"><input data-testid="m-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
          <Field label="State"><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
          <Field label="Tier">
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Monthly footfall"><input type="number" value={form.monthly_footfall} onChange={(e) => setForm({ ...form, monthly_footfall: e.target.value })} /></Field>
          <Field label="Area (sqft)"><input type="number" value={form.area_sqft} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} placeholder="Leave blank — AI can fill" /></Field>
          <Field label="Address" full><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          <Field label="Description" full><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-3 justify-end">
          <button onClick={onClose} className="text-sm text-neutral-400 hover:text-white px-4 py-2">Cancel</button>
          <button onClick={create} disabled={saving} className="btn-secondary" data-testid="m-save">
            {saving ? "Saving…" : "Save as draft"}
          </button>
          <button onClick={createAndIngest} disabled={saving || aiRunning} className="btn-primary" data-testid="m-save-ingest">
            <Sparkles size={14} /> {aiRunning ? "AI ingesting…" : "Save + AI Ingest"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
